import React, { useState, useEffect } from 'react';
import { ref, get, set, update } from 'firebase/database';
import { realtimeDb } from '../firebase';
import './TeacherDashboard.css';

interface TeacherData {
  name: string;
  code: string;
  classes?: string[];
}

interface ClassData {
  id: string;
  name: string;
  studentCount?: number;
}

interface StudentData {
  name: string;
  englishName: string;
  class?: string;
  code: string;
  id?: string;
}

interface ReportCardData {
  month: string;
  content: string;
  teacherName: string;
  createdAt: string;
  reply?: string;
  replyAuthor?: string;
  replyCreatedAt?: string;
  replyUpdatedAt?: string;
  teacherReadAt?: string;
}

// TeacherClassGroup 컴포넌트
interface TeacherClassGroupProps {
  classItem: ClassData;
  onStudentSelect: (student: StudentData) => void;
}

const TeacherClassGroup: React.FC<TeacherClassGroupProps> = ({ 
  classItem, 
  onStudentSelect
}) => {
  const [classStudents, setClassStudents] = useState<StudentData[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    if (classItem.id) {
      loadClassStudents();
    }
  }, [classItem.id]);

  const loadClassStudents = async () => {
    setLoadingStudents(true);
    try {
      const classStudentsRef = ref(realtimeDb, `classes/${classItem.id}/students`);
      const snapshot = await get(classStudentsRef);
      
      if (snapshot.exists()) {
        const studentsData = snapshot.val();
        const studentsArray = Object.entries(studentsData).map(([code, data]: [string, any]) => ({
          id: code,
          code,
          name: data.name || 'No Name',
          englishName: data.englishName || 'No English Name',
          class: data.class || classItem.id
        }));
        setClassStudents(studentsArray);
      } else {
        setClassStudents([]);
      }
    } catch (error) {
      console.error('반 학생 목록 불러오기 실패:', error);
      setClassStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  return (
    <div className="class-group">
      <div className="class-group-header">
        <div className="class-group-title-row">
          <div className="class-group-info">
            <h4 className="class-group-title">
              {classItem.name}
              <span className="student-count">({classStudents.length}명)</span>
            </h4>
          </div>
        </div>
      </div>
      <div className="students-table-container">
        {loadingStudents ? (
          <div className="loading">Loading student list...</div>
        ) : classStudents.length > 0 ? (
          <table className="students-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>English Name</th>
                <th>Code</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {classStudents
                .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                .map((student) => {
                  return (
                    <tr key={student.code} className="student-row">
                      <td>{student.name}</td>
                      <td>{student.englishName}</td>
                      <td>{student.code}</td>
                      <td>
                        <button 
                          onClick={() => onStudentSelect(student)}
                          className="report-button"
                          title="Write Report"
                        >
                          Write Report
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        ) : (
          <div className="no-students">
            <p>No students are registered in this class.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const TeacherDashboard: React.FC = () => {
  // 현재 년도와 월 가져오기
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-11
  
  // 월 선택 옵션 (3월부터 시작)
  const months = [
    'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
    'January', 'February'
  ];
  
  // 년도 옵션 (현재 년도부터 시작)
  const [availableYears, setAvailableYears] = useState<number[]>([currentYear]);

  const [teacherCode, setTeacherCode] = useState('');
  const [teacherData, setTeacherData] = useState<TeacherData | null>(null);
  const [managedClasses, setManagedClasses] = useState<ClassData[]>([]);
  const [selectedClass, setSelectedClass] = useState<ClassData | null>(null);
  const [classStudents, setClassStudents] = useState<StudentData[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentData | null>(null);
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [reportContent, setReportContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [error, setError] = useState('');
  const [teacherError, setTeacherError] = useState('');
  const [showReportForm, setShowReportForm] = useState(false);
  const [isExistingReport, setIsExistingReport] = useState(false);
  const [showStudentsTable, setShowStudentsTable] = useState(false);
  
  // 답장 관련 state
  const [replyData, setReplyData] = useState<{
    reply?: string;
    replyAuthor?: string;
    replyCreatedAt?: string;
    replyUpdatedAt?: string;
    teacherReadAt?: string;
  } | null>(null);
  const [replyStatus, setReplyStatus] = useState<'none' | 'new' | 'updated' | 'read'>('none');
  
  // 월별 리포트 및 답장 상태
  const [monthReportStatuses, setMonthReportStatuses] = useState<Record<string, {
    hasReport: boolean;
    replyStatus: 'none' | 'new' | 'updated' | 'read';
  }>>({});
  
  // Class filtering state
  const [selectedClassFilter, setSelectedClassFilter] = useState<string>('all');

  // Teacher code authentication and managed classes loading
  const handleTeacherCodeSubmit = async () => {
    if (!teacherCode.trim()) {
      setTeacherError('Please enter teacher code.');
      return;
    }

    setTeacherLoading(true);
    setTeacherError('');
    
    try {
      // 먼저 teachers/code/{teacherCode}에서 선생님 정보 확인
      const teacherRef = ref(realtimeDb, `teachers/code/${teacherCode}`);
      const teacherSnapshot = await get(teacherRef);
      
      console.log('선생님 정보 조회 결과:', teacherSnapshot.val());
      
      if (teacherSnapshot.exists()) {
        const data = teacherSnapshot.val();
        setTeacherData({
          name: data.name,
          code: teacherCode,
          classes: data.classes || []
        });
        setError('');
        
        console.log('선생님 데이터:', data);
        
        // 해당 선생님이 관리하는 반 목록 불러오기
        // teachers/code/{teacherCode}/classes 경로에서 반 목록 가져오기
        await loadTeacherClasses(teacherCode);
      } else {
        setTeacherError('Invalid teacher code.');
        setTeacherData(null);
        setManagedClasses([]);
      }
    } catch (error) {
      console.error('선생님 정보 조회 실패:', error);
      setTeacherError('Error occurred while fetching information.');
      setTeacherData(null);
      setManagedClasses([]);
    } finally {
      setTeacherLoading(false);
    }
  };

  // Load classes managed by teacher (teachers/code/{teacherCode}/classes)
  const loadTeacherClasses = async (teacherCode: string) => {
    setClassesLoading(true);
    try {
      console.log(`선생님 ${teacherCode}의 반 목록 불러오기 시작`);
      
      // teachers/code/{teacherCode}/classes 경로에서 반 목록 가져오기
      const teacherClassesRef = ref(realtimeDb, `teachers/code/${teacherCode}/classes`);
      const teacherClassesSnapshot = await get(teacherClassesRef);
      
      console.log('teachers/code/{teacherCode}/classes 결과:', teacherClassesSnapshot.val());
      
      if (teacherClassesSnapshot.exists()) {
        const classesData = teacherClassesSnapshot.val();
        const classNames = Object.keys(classesData);
        console.log('찾은 반들:', classNames);
        
        if (classNames.length > 0) {
          // 각 반의 정보와 학생 수를 가져오기
          const classesDataArray: ClassData[] = [];
          
          for (const className of classNames) {
            try {
              const classInfo = classesData[className];
              console.log(`반 ${className} 정보:`, classInfo);
              
              // 해당 반의 학생 수 확인 (classes/{className}/students)
              const classStudentsRef = ref(realtimeDb, `classes/${className}/students`);
              const studentsSnapshot = await get(classStudentsRef);
              const studentCount = studentsSnapshot.exists() ? Object.keys(studentsSnapshot.val()).length : 0;
              
              classesDataArray.push({
                id: className,
                name: classInfo.name || className,
                studentCount
              });
            } catch (error) {
              console.error(`반 ${className} 정보 처리 실패:`, error);
              // 에러가 발생해도 다른 반 정보는 계속 처리
              classesDataArray.push({
                id: className,
                name: className,
                studentCount: 0
              });
            }
          }
          
          console.log('최종 반 데이터:', classesDataArray);
          setManagedClasses(classesDataArray);
        } else {
          console.log('담당 반이 없음');
          setManagedClasses([]);
        }
      } else {
        console.log('teachers/code/{teacherCode}/classes 노드가 존재하지 않음');
        setManagedClasses([]);
      }
    } catch (error) {
      console.error('선생님 반 목록 불러오기 실패:', error);
      setManagedClasses([]);
    } finally {
      setClassesLoading(false);
    }
  };

  // 답장 상태 계산 함수
  const calculateReplyStatus = (report: any): 'none' | 'new' | 'updated' | 'read' => {
    if (!report.reply) {
      return 'none';
    }
    
    if (!report.teacherReadAt) {
      // 읽지 않은 새 답장
      return 'new';
    }
    
    if (report.replyUpdatedAt && report.teacherReadAt) {
      // replyUpdatedAt이 teacherReadAt보다 나중이면 수정된 답장
      const replyUpdatedTime = new Date(report.replyUpdatedAt).getTime();
      const teacherReadTime = new Date(report.teacherReadAt).getTime();
      
      if (replyUpdatedTime > teacherReadTime) {
        return 'updated';
      }
    }
    
    // 읽은 답장 (수정 없음)
    return 'read';
  };

  // 학생의 전체 답장 상태 계산 및 저장 (모든 년도/월 확인)
  const calculateAndSaveStudentReplyStatus = async (studentCode: string) => {
    try {
      const reportsRef = ref(realtimeDb, `students/code/${studentCode}/reports`);
      const reportsSnapshot = await get(reportsRef);
      
      let overallStatus: 'new' | 'updated' | 'read' = 'read';
      let hasNewReply = false;
      let hasUpdatedReply = false;
      
      if (reportsSnapshot.exists()) {
        const allReportsData = reportsSnapshot.val();
        
        // 모든 년도 확인
        for (const year in allReportsData) {
          const yearData = allReportsData[year];
          
          // 모든 월 확인
          for (const month in yearData) {
            const report = yearData[month];
            const monthStatus = calculateReplyStatus(report);
            
            console.log(`학생 ${studentCode} ${year}년 ${month}월 답장 상태:`, {
              monthStatus,
              hasReply: !!report.reply,
              teacherReadAt: report.teacherReadAt,
              replyUpdatedAt: report.replyUpdatedAt
            });
            
            // 전체 상태 결정 (최우선순위: new > updated > read)
            if (monthStatus === 'new') {
              hasNewReply = true;
              console.log(`학생 ${studentCode} 새 답장 발견: ${year}년 ${month}월`);
              break; // 새 답장이 있으면 즉시 중단
            } else if (monthStatus === 'updated') {
              hasUpdatedReply = true;
              console.log(`학생 ${studentCode} 수정된 답장 발견: ${year}년 ${month}월`);
              // break하지 않고 계속 확인 (새 답장이 더 우선순위가 높으므로)
            }
          }
          
          // 새 답장이 있으면 더 이상 확인할 필요 없음
          if (hasNewReply) {
            break;
          }
        }
        
        // 전체 상태 결정
        if (hasNewReply) {
          overallStatus = 'new';
        } else if (hasUpdatedReply) {
          overallStatus = 'updated';
        } else {
          overallStatus = 'read';
        }
      }
      
      // 전체 상태 저장 (년도 구분 없이 하나만)
      const statusRef = ref(realtimeDb, `students/code/${studentCode}/replyStatus`);
      await set(statusRef, overallStatus);
      
      console.log(`학생 ${studentCode} 답장 상태 저장 완료:`, {
        overallStatus,
        hasNewReply,
        hasUpdatedReply,
        path: `students/code/${studentCode}/replyStatus`
      });
      
      return overallStatus;
    } catch (error) {
      console.error(`학생 ${studentCode} 답장 상태 계산 및 저장 실패:`, error);
      return 'read';
    }
  };

  // 반 선택 시 해당 반의 학생 목록 불러오기
  const handleClassSelect = async (classData: ClassData) => {
    setSelectedClass(classData);
    setShowStudentsTable(true);
    setStudentsLoading(true);
    
    try {
      const classStudentsRef = ref(realtimeDb, `classes/${classData.id}/students`);
      const studentsSnapshot = await get(classStudentsRef);
      
      console.log(`반 ${classData.id}의 학생 데이터:`, studentsSnapshot.val());
      
      if (studentsSnapshot.exists()) {
        const studentsData = studentsSnapshot.val();
        const studentsArray = Object.entries(studentsData).map(([studentCode, data]: [string, any]) => {
          console.log(`학생 ${studentCode}의 데이터:`, data);
          
          return {
            id: studentCode, // studentCode를 id로 사용
            code: studentCode, // studentCode가 학생 코드
            name: data.name || 'No Name',
            englishName: data.englishName || 'No English Name',
            class: data.class || classData.id
          };
        });
        
        console.log('처리된 학생 배열:', studentsArray);
        setClassStudents(studentsArray);
      } else {
        setClassStudents([]);
      }
    } catch (error) {
      console.error('학생 목록 불러오기 실패:', error);
      setClassStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  };

  // Open report form modal when student is selected
  const openReportForm = async (student: StudentData) => {
    setSelectedStudent(student);
    setShowReportForm(true);
    const year = currentYear.toString();
    setSelectedYear(year);
    setSelectedMonth('');
    setReportContent('');
    setError('');
    setIsExistingReport(false);
    setReplyData(null);
    setReplyStatus('none');
    
    // Load existing report years for the student
    await loadAvailableYears(student.code);
    // 월별 리포트 상태 로드 (year와 student 파라미터 직접 전달하여 state 업데이트 타이밍 문제 해결)
    await loadMonthReportStatuses(year, student);
  };

  // Load existing report years for the student
  const loadAvailableYears = async (studentCode: string) => {
    try {
      const reportsRef = ref(realtimeDb, `students/code/${studentCode}/reports`);
      const snapshot = await get(reportsRef);
      
      if (snapshot.exists()) {
        const reportsData = snapshot.val();
        const existingYears = Object.keys(reportsData).map(year => parseInt(year)).sort((a, b) => b - a); // 내림차순 정렬
        
        // 현재 년도가 없으면 추가
        if (!existingYears.includes(currentYear)) {
          existingYears.unshift(currentYear);
        }
        
        setAvailableYears(existingYears);
      } else {
        // 리포트가 없으면 현재 년도만
        setAvailableYears([currentYear]);
      }
    } catch (error) {
      console.error('기존 리포트 년도 불러오기 실패:', error);
      setAvailableYears([currentYear]);
    }
  };

  // 월별 리포트 및 답장 상태 로드
  const loadMonthReportStatuses = async (year?: string, student?: StudentData | null) => {
    const yearToUse = year || selectedYear;
    const studentToUse = student ?? selectedStudent;
    
    if (!studentToUse || !yearToUse) {
      // 초기화
      const emptyStatuses: Record<string, {
        hasReport: boolean;
        replyStatus: 'none' | 'new' | 'updated' | 'read';
      }> = {};
      for (const month of months) {
        emptyStatuses[month] = { hasReport: false, replyStatus: 'none' };
      }
      setMonthReportStatuses(emptyStatuses);
      return;
    }
    
    try {
      const reportsRef = ref(realtimeDb, `students/code/${studentToUse.code}/reports/${yearToUse}`);
      const snapshot = await get(reportsRef);
      
      const statuses: Record<string, {
        hasReport: boolean;
        replyStatus: 'none' | 'new' | 'updated' | 'read';
      }> = {};
      
      // 모든 월을 기본값으로 초기화
      for (const month of months) {
        statuses[month] = { hasReport: false, replyStatus: 'none' };
      }
      
      if (snapshot.exists()) {
        const reportsData = snapshot.val();
        console.log('리포트 데이터:', reportsData);
        
        // 각 월 확인
        for (const month of months) {
          const report = reportsData[month];
          if (report) {
            // 리포트가 있으면 content가 있는지 확인
            const hasReport = !!(report.content && report.content.trim());
            let replyStatus: 'none' | 'new' | 'updated' | 'read' = 'none';
            
            // 답장 상태 계산 (저장하지 않고 즉시 계산)
            replyStatus = calculateReplyStatus(report);
            
            statuses[month] = { hasReport, replyStatus };
            console.log(`월 ${month}: hasReport=${hasReport}, replyStatus=${replyStatus}`);
          }
        }
      }
      
      console.log('최종 상태:', statuses);
      setMonthReportStatuses(statuses);
    } catch (error) {
      console.error('월별 리포트 상태 로드 실패:', error);
    }
  };

  // Reset existing report content when year is selected
  const handleYearChange = async (year: string) => {
    setSelectedYear(year);
    setSelectedMonth('');
    setReportContent('');
    setIsExistingReport(false);
    setReplyData(null);
    setReplyStatus('none');
    // year 변경 후에는 state가 업데이트되기 전이므로 명시적으로 전달
    await loadMonthReportStatuses(year, selectedStudent);
  };

  // Load existing report content when month is selected
  const handleMonthChange = async (month: string) => {
    setSelectedMonth(month);
    
    if (month && selectedStudent && selectedYear) {
      try {
        const reportRef = ref(realtimeDb, `students/code/${selectedStudent.code}/reports/${selectedYear}/${month}`);
        const snapshot = await get(reportRef);
        
        if (snapshot.exists()) {
          const existingReport = snapshot.val();
          setReportContent(existingReport.content || '');
          setIsExistingReport(true);
          
          // 답장 정보 불러오기
          const replyInfo = {
            reply: existingReport.reply,
            replyAuthor: existingReport.replyAuthor,
            replyCreatedAt: existingReport.replyCreatedAt,
            replyUpdatedAt: existingReport.replyUpdatedAt,
            teacherReadAt: existingReport.teacherReadAt
          };
          setReplyData(replyInfo.reply ? replyInfo : null);
          
          // 답장 상태 계산
          if (!replyInfo.reply) {
            setReplyStatus('none');
          } else if (!replyInfo.teacherReadAt) {
            setReplyStatus('new');
          } else if (replyInfo.replyUpdatedAt && replyInfo.replyUpdatedAt > replyInfo.teacherReadAt) {
            setReplyStatus('updated');
          } else {
            setReplyStatus('read');
          }
        } else {
          setReportContent('');
          setIsExistingReport(false);
          setReplyData(null);
          setReplyStatus('none');
        }
        
        // 월별 리포트 상태 다시 로드 (업데이트된 정보 반영)
        // 명시적으로 year와 student를 전달하여 최신 데이터 사용
        await loadMonthReportStatuses(selectedYear, selectedStudent);
      } catch (error) {
        console.error('기존 리포트 불러오기 실패:', error);
        setIsExistingReport(false);
        setReplyData(null);
        setReplyStatus('none');
      }
    }
  };
  
  // 리포트 모달 닫기
  const handleCloseReportForm = async () => {
    setShowReportForm(false);
    // 월별 리포트 상태 다시 로드 (답장 수정 후 상태 업데이트)
    if (selectedStudent && selectedYear) {
      await loadMonthReportStatuses(selectedYear, selectedStudent);
    }
  };

  // 답장 읽음 처리
  const markReplyAsRead = async () => {
    if (!selectedStudent || !selectedYear || !selectedMonth || !replyData) return;
    
    try {
      const reportRef = ref(realtimeDb, `students/code/${selectedStudent.code}/reports/${selectedYear}/${selectedMonth}`);
      await update(reportRef, {
        teacherReadAt: new Date().toISOString()
      });
      
      // 답장 상태 다시 계산 및 저장 (모든 년도/월 확인)
      await calculateAndSaveStudentReplyStatus(selectedStudent.code);
      
      setReplyStatus('read');
      if (replyData) {
        setReplyData({
          ...replyData,
          teacherReadAt: new Date().toISOString()
        });
      }
      
      // 월별 리포트 상태 다시 로드 (명시적으로 파라미터 전달)
      await loadMonthReportStatuses(selectedYear, selectedStudent);
    } catch (error) {
      console.error('답장 읽음 처리 실패:', error);
    }
  };

  // Report Card 저장
  const handleReportSubmit = async () => {
    if (!selectedYear || !selectedMonth || !reportContent.trim()) {
      setError('Please select year, month and enter content.');
      return;
    }

    if (!teacherData || !selectedStudent) {
      setError('Teacher and student information is required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const reportRef = ref(realtimeDb, `students/code/${selectedStudent.code}/reports/${selectedYear}/${selectedMonth}`);
      
      // 기존 리포트가 있는지 확인
      const existingSnapshot = await get(reportRef);
      const isUpdate = existingSnapshot.exists();
      const existingData = existingSnapshot.exists() ? existingSnapshot.val() : {};
      
      // 리포트 데이터 구성 (undefined 값 제외)
      const reportData: any = {
        content: reportContent.trim(),
        teacherName: teacherData.name,
        year: selectedYear,
        month: selectedMonth,
        createdAt: existingData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isUpdate: isUpdate
      };
      
      // 기존 답장 정보가 있으면 유지 (undefined 값 제외)
      if (existingData.reply !== undefined) {
        reportData.reply = existingData.reply;
      }
      if (existingData.replyAuthor !== undefined) {
        reportData.replyAuthor = existingData.replyAuthor;
      }
      if (existingData.replyCreatedAt !== undefined) {
        reportData.replyCreatedAt = existingData.replyCreatedAt;
      }
      if (existingData.replyUpdatedAt !== undefined) {
        reportData.replyUpdatedAt = existingData.replyUpdatedAt;
      }
      if (existingData.teacherReadAt !== undefined) {
        reportData.teacherReadAt = existingData.teacherReadAt;
      }
      
      await set(reportRef, reportData);

      // 월별 리포트 상태 다시 로드 (명시적으로 파라미터 전달)
      await loadMonthReportStatuses(selectedYear, selectedStudent);

      // 성공 후 폼 초기화
      setReportContent('');
      setSelectedMonth('');
      setError('');
      setIsExistingReport(false);
      
      const message = isUpdate ? 'Report Card has been successfully updated!' : 'Report Card has been successfully saved!';
      alert(message);
      
      // 모달 닫기
      setShowReportForm(false);
    } catch (error) {
      console.error('Report Card 저장 실패:', error);
      setError('Error occurred while saving.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="teacher-dashboard">
      <h2 className="dashboard-title">Teacher Dashboard</h2>
      
      {/* 선생님 코드 입력 섹션 */}
      <div className="teacher-code-section">
        <h3>Teacher Login Code</h3>
        <div className="code-input-container">
          <input
            type="text"
            value={teacherCode}
            onChange={(e) => setTeacherCode(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleTeacherCodeSubmit()}
            placeholder="Enter teacher code..."
            className="code-input"
            disabled={teacherLoading}
          />
          <button 
            onClick={handleTeacherCodeSubmit}
            className="code-submit-button"
            disabled={teacherLoading || !teacherCode.trim()}
          >
            {teacherLoading ? 'Loading...' : 'Search'}
          </button>
        </div>
        
        {teacherError && (
          <div className="error-message">
            {teacherError}
          </div>
        )}
        
        {teacherData && (
          <div className="teacher-info-display">
            <h4>Teacher Information</h4>
            <div className="teacher-details">
              <p><strong>Name:</strong> {teacherData.name}</p>
              <p><strong>Code:</strong> {teacherData.code}</p>
            </div>
          </div>
        )}
      </div>

      {/* Managed classes section - displayed only after teacher authentication */}
      {teacherData && (
        <div className="managed-classes-section">
          <h3>Managed Classes</h3>
          
          {classesLoading ? (
            <div className="loading">Loading class list...</div>
          ) : managedClasses.length > 0 ? (
            <>
              {/* Class filtering dropdown */}
              <div className="class-filter-section">
                <label htmlFor="classFilter" className="filter-label">
                  Class Filter:
                </label>
                <select
                  id="classFilter"
                  value={selectedClassFilter}
                  onChange={(e) => setSelectedClassFilter(e.target.value)}
                  className="class-filter-dropdown"
                >
                  <option value="all">View All Classes</option>
                  {managedClasses
                    .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                    .map((classItem) => (
                      <option key={classItem.id} value={classItem.id}>
                        {classItem.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="classes-grouped-container">
                {managedClasses
                  .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                  .filter((classItem) => 
                    selectedClassFilter === 'all' || classItem.id === selectedClassFilter
                  )
                  .map((classItem) => (
                    <TeacherClassGroup 
                      key={classItem.id}
                      classItem={classItem}
                      onStudentSelect={openReportForm}
                    />
                  ))}
              </div>
            </>
          ) : (
            <div className="no-classes">
              <p>No classes are currently assigned.</p>
            </div>
          )}
        </div>
      )}


      {/* Report Card Writing Modal */}
      {showReportForm && teacherData && selectedStudent && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Write Report Card for {selectedStudent.name}</h3>
              <button 
                className="modal-close-button" 
                onClick={handleCloseReportForm}
              >
                ×
              </button>
            </div>
            
            <div className="report-form">
              <div className="student-info-summary">
                <h4>Student Information</h4>
                <p><strong>Name:</strong> {selectedStudent.name} ({selectedStudent.englishName})</p>
                {selectedStudent.class && <p><strong>Class:</strong> {selectedStudent.class}</p>}
                <p><strong>Code:</strong> {selectedStudent.code}</p>
              </div>

              <div className="form-group">
                <label htmlFor="year">Select Year *</label>
                <select
                  id="year"
                  value={selectedYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                  className="year-select"
                >
                                     {availableYears.map(year => (
                     <option key={year} value={year}>{year}</option>
                   ))}
                </select>
              </div>

              {/* 월 선택 그리드 */}
              <div className="month-selection">
                <h3>Select Month</h3>
                <div className="month-buttons">
                  {months.map(month => {
                    const monthStatus = monthReportStatuses[month] || { hasReport: false, replyStatus: 'none' };
                    return (
                      <button
                        key={month}
                        onClick={() => handleMonthChange(month)}
                        className={`month-button ${selectedMonth === month ? 'active' : ''} ${monthStatus.hasReport ? 'has-report' : ''} ${monthStatus.replyStatus !== 'none' ? 'has-reply' : ''}`}
                        type="button"
                      >
                        <span className="month-name">{month}</span>
                        {monthStatus.replyStatus !== 'none' && (
                          <span 
                            className={`reply-status-dot reply-status-${monthStatus.replyStatus}`}
                            title={
                              monthStatus.replyStatus === 'new' ? 'New Reply' :
                              monthStatus.replyStatus === 'updated' ? 'Updated Reply' :
                              'Read Reply'
                            }
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="content">Teacher's comment *</label>
                <textarea
                  id="content"
                  value={reportContent}
                  onChange={(e) => setReportContent(e.target.value)}
                  placeholder="Please write about the student's learning situation and achievements..."
                  className="content-textarea"
                  rows={8}
                />
              </div>

              {error && (
                <div className={isExistingReport ? 'info-message' : 'error-message'}>
                  {error}
                </div>
              )}

              {/* 답장 섹션 */}
              {selectedMonth && (
                <div className="reply-section">
                  <div className="reply-section-header">
                    <h4>Parent's Reply</h4>
                    {replyStatus !== 'none' && (
                      <span 
                        className={`reply-status-dot reply-status-${replyStatus}`}
                        title={
                          replyStatus === 'new' ? 'New Reply' :
                          replyStatus === 'updated' ? 'Updated Reply' :
                          'Read Reply'
                        }
                      />
                    )}
                  </div>
                  {replyData && replyData.reply ? (
                    <div className="reply-content-box">
                      <div className="reply-meta">
                        <span className="reply-author">{replyData.replyAuthor || 'Parent'}</span>
                        <span className="reply-date">
                          {replyData.replyUpdatedAt 
                            ? new Date(replyData.replyUpdatedAt).toLocaleString('ko-KR')
                            : replyData.replyCreatedAt 
                            ? new Date(replyData.replyCreatedAt).toLocaleString('ko-KR')
                            : ''}
                        </span>
                      </div>
                      <div className="reply-text">{replyData.reply}</div>
                      {(replyStatus === 'new' || replyStatus === 'updated') && (
                        <button 
                          onClick={markReplyAsRead}
                          className="mark-read-button"
                        >
                          Mark as Read
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="no-reply-message">No reply yet.</div>
                  )}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseReportForm}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleReportSubmit}
                  className="submit-button"
                  disabled={loading || !selectedYear || !selectedMonth || !reportContent.trim()}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDashboard;
